import * as React from 'react';

import { useCallbackRef } from './useCallbackRef';

export interface UseClickOutsideOptions {
  /** Refs whose elements should not count as "outside" (e.g. the trigger). */
  ignoredRefs?: React.RefObject<HTMLElement | null>[];
}

function isInsideIgnored(target: Node, ignoredRefs: React.RefObject<HTMLElement | null>[]): boolean {
  for (const r of ignoredRefs) {
    const node = r.current;
    if (node && node.contains(target)) {
      return true;
    }
  }
  return false;
}

export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
  active = true,
  options?: UseClickOutsideOptions,
): void {
  const onOutside = useCallbackRef(handler);
  const ignoredRefsRef = React.useRef<React.RefObject<HTMLElement | null>[]>([]);
  ignoredRefsRef.current = options?.ignoredRefs ?? [];

  React.useEffect(() => {
    if (!active) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (isInsideIgnored(target, ignoredRefsRef.current)) return;
      if (!el.contains(target)) {
        onOutside();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [active, ref, onOutside]);
}
